import { useCallback, useEffect, useState } from 'react';
import { Confirm, toast, type Destructive } from '@drbell/ui';
import { api, ApiError, type Appointment, type PatientSummary } from '../api.ts';

/**
 * Appointments — RX-RX-05, parity with the phone.
 *
 * Families book the follow-up, the blood test and the scan, and until now
 * could only write them down from the phone. What the family types is stored
 * as typed: DrBell does not name the test, the doctor or the reason.
 *
 * Reminders go out the day before and on the morning, as on the phone.
 */
const KIND: Record<Appointment['kind'], string> = {
  doctor_visit: 'Doctor visit',
  lab_test: 'Test',
  procedure: 'Procedure',
  other: 'Other',
};

const reason = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));

/** Today's date where the PATIENT is, as YYYY-MM-DD. */
function todayIn(timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(Date.now());
}

function readable(a: Appointment): string {
  const date = new Date(`${a.localDate}T00:00:00Z`).toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
  return a.localTime ? `${date}, ${a.localTime}` : date;
}

export function Appointments({ patient, canEdit }: { patient: PatientSummary; canEdit: boolean }) {
  const [items, setItems] = useState<Appointment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Destructive | null>(null);
  const today = todayIn(patient.timezone);

  const load = useCallback(async () => {
    try {
      setItems((await api.get<{ appointments: Appointment[] }>(`/api/patients/${patient.id}/appointments`)).appointments);
    } catch (e) {
      setError(reason(e));
    }
  }, [patient.id]);

  useEffect(() => {
    setItems(null);
    void load();
  }, [load]);

  const resolve = async (a: Appointment, status: 'attended' | 'cancelled') => {
    try {
      await api.post(`/api/patients/${patient.id}/appointments/${a.id}/resolve`, { status });
      toast.success(status === 'attended' ? 'Marked as done' : 'Cancelled');
      await load();
    } catch (e) {
      toast.error('Could not save', reason(e));
    }
  };

  if (error) return <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</p>;
  if (!items) return <p className="text-(--muted)">Loading…</p>;

  const upcoming = items
    .filter((a) => a.status === 'scheduled')
    .sort((x, y) => `${x.localDate}${x.localTime ?? ''}`.localeCompare(`${y.localDate}${y.localTime ?? ''}`));
  const past = items.filter((a) => a.status !== 'scheduled');

  return (
    <section className="space-y-6">
      {upcoming.length === 0 ? (
        <p className="rounded-xl border border-dashed border-(--line) px-4 py-8 text-center text-(--muted)">
          No appointments coming up.
        </p>
      ) : (
        <ul className="divide-y divide-(--line) rounded-xl border border-(--line)">
          {upcoming.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{a.title}</p>
                <p className="text-sm text-(--muted)">
                  {readable(a)} · {KIND[a.kind]}
                  {a.doctorName && ` · ${a.doctorName}`}
                  {a.locationNote && ` · ${a.locationNote}`}
                  {a.localDate < today && <span className="text-amber-600"> · date has passed</span>}
                </p>
                {a.notes && <p className="mt-1 text-sm">{a.notes}</p>}
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  <button onClick={() => void resolve(a, 'attended')} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)">
                    Went
                  </button>
                  <button
                    onClick={() =>
                      setPending({
                        title: `Cancel “${a.title}”?`,
                        body: 'Its reminders stop. It stays in the list of past appointments.',
                        confirmLabel: 'Cancel appointment',
                        onConfirm: async () => {
                          setPending(null);
                          await resolve(a, 'cancelled');
                        },
                      })
                    }
                    className="min-h-11 rounded-lg border border-(--line) px-3 text-sm text-(--muted)"
                  >
                    Cancel…
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && <AddAppointment patientId={patient.id} today={today} onAdded={load} />}

      {past.length > 0 && (
        <details className="rounded-xl border border-(--line) p-4">
          <summary className="cursor-pointer text-sm text-(--muted)">Past ({past.length})</summary>
          <ul className="mt-3 space-y-1 text-sm">
            {past.map((a) => (
              <li key={a.id}>
                {readable(a)} — {a.title} <span className="text-(--muted)">({a.status})</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </section>
  );
}

function AddAppointment({ patientId, today, onAdded }: { patientId: string; today: string; onAdded: () => Promise<void> }) {
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<Appointment['kind']>('doctor_visit');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [doctor, setDoctor] = useState('');
  const [place, setPlace] = useState('');
  const [busy, setBusy] = useState(false);

  const valid = title.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(date) && date >= today;
  const field = 'rounded-lg border border-(--line) bg-(--bg) min-h-11 px-3 text-sm';

  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/api/patients/${patientId}/appointments`, {
        kind,
        title: title.trim(),
        localDate: date,
        localTime: time || null,
        ...(doctor.trim() ? { doctorName: doctor.trim() } : {}),
        ...(place.trim() ? { locationNote: place.trim() } : {}),
        // The day before and the morning itself, as the phone does.
        remindDaysBefore: [1, 0],
      });
      toast.success('Appointment added', 'Reminders go out the day before and on the morning.');
      setTitle('');
      setDate('');
      setTime('');
      setDoctor('');
      setPlace('');
      await onAdded();
    } catch (e) {
      toast.error('Could not add it', reason(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-(--line) p-4">
      <h2 className="text-sm font-medium">Add an appointment</h2>
      <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="What it is, e.g. Follow-up with Dr Rao" aria-label="What it is" className={`${field} w-full`} />
      <div className="flex flex-wrap gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value as Appointment['kind'])} aria-label="Kind" className={field}>
          {Object.entries(KIND).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
        <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} aria-label="Date" className={field} />
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Time (optional)" className={field} />
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={doctor} onChange={(e) => setDoctor(e.target.value)} maxLength={200} placeholder="Doctor (optional)" aria-label="Doctor" className={`${field} flex-1`} />
        <input value={place} onChange={(e) => setPlace(e.target.value)} maxLength={300} placeholder="Where (optional)" aria-label="Where" className={`${field} flex-1`} />
      </div>
      <button disabled={!valid || busy} onClick={() => void add()} className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40">
        {busy ? 'Adding…' : 'Add'}
      </button>
    </div>
  );
}
